import type { CanvasPanel } from "../state/canvas";
import { CarSpecPanel } from "./canvas/CarSpecPanel";
import { CoveragePanel } from "./canvas/CoveragePanel";
import { DynamicsPanel } from "./canvas/DynamicsPanel";
import { FindingsPanel } from "./canvas/FindingsPanel";
import { RawPanel } from "./canvas/RawPanel";
import { ScenesPanel } from "./canvas/ScenesPanel";
import { ScoresPanel } from "./canvas/ScoresPanel";
import { StagesPanel } from "./canvas/StagesPanel";

interface CanvasViewProps {
  panels: CanvasPanel[];
  isOpen: boolean;
  onClose: () => void;
}

export function CanvasView({ panels, isOpen, onClose }: CanvasViewProps) {
  return (
    <aside className={`canvas-pane ${isOpen ? "open" : ""}`} aria-hidden={!isOpen}>
      <header className="canvas-header">
        <div>
          <p className="eyebrow">system view</p>
          <h2>数据视图</h2>
        </div>
        <button className="icon-button mobile-only" type="button" onClick={onClose}>
          关闭
        </button>
      </header>
      {panels.length === 0 ? (
        <div className="canvas-empty">
          <p className="muted">等待车大师调用工具…</p>
          <p className="muted small">
            问一个能触发模拟的问题，例如 “帮我评估 BRZ 跑山路改装方案”。
          </p>
        </div>
      ) : (
        <div className="canvas-panels">
          {panels.map((panel) => (
            <PanelCard key={panel.id} panel={panel} />
          ))}
        </div>
      )}
    </aside>
  );
}

function PanelCard({ panel }: { panel: CanvasPanel }) {
  return (
    <section className={`canvas-card${panel.stale ? " stale" : ""}`}>
      <header className="canvas-card-header">
        <div>
          <p className="eyebrow">{panel.source}</p>
          <h3>{panel.title}</h3>
        </div>
        <small className="muted">{formatTime(panel.timestamp)}</small>
      </header>
      <div className="canvas-card-body">
        <PanelBody panel={panel} />
      </div>
    </section>
  );
}

function PanelBody({ panel }: { panel: CanvasPanel }) {
  if (panel.stale) {
    return <RawPanel payload={panel.payload} stale />;
  }
  switch (panel.kind) {
    case "scores":
      return <ScoresPanel payload={panel.payload} />;
    case "scenes":
      return <ScenesPanel payload={panel.payload} />;
    case "dynamics":
      return <DynamicsPanel payload={panel.payload} />;
    case "findings":
      return <FindingsPanel payload={panel.payload} />;
    case "coverage":
      return <CoveragePanel payload={panel.payload} />;
    case "stages":
      return <StagesPanel payload={panel.payload} />;
    case "carspec":
      return <CarSpecPanel payload={panel.payload} />;
    case "raw":
    default:
      return <RawPanel payload={panel.payload} />;
  }
}

function formatTime(iso: string): string {
  try {
    return new Intl.DateTimeFormat("zh-CN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(new Date(iso));
  } catch {
    return "";
  }
}
